import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, MapPin, Calendar, Ruler, Clock, CheckCircle2, X,
  ChevronLeft, ChevronRight, Share2, Loader2, Building2, Hammer,
} from "lucide-react";
import { fetchRealization, photoUrl } from "../lib/api";
import { categoryLabel } from "../data/realizations";
import { toast } from "sonner";

const BUSINESS_WHATSAPP = "22392001010";

function InfoItem({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gold/15 text-gold-700"><Icon size={17} /></span>
      <div>
        <div className="text-xs uppercase tracking-wide text-navy/45">{label}</div>
        <div className="font-medium text-navy">{value}</div>
      </div>
    </div>
  );
}

function GallerySection({ title, photos, onOpen }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mt-10">
      <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold text-navy">
        <span className="h-4 w-1 rounded-full bg-gold" /> {title}
      </h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {photos.map((ph, idx) => (
          <button
            key={ph}
            data-testid={`gallery-photo-${title}-${idx}`}
            onClick={() => onOpen(ph)}
            className="group relative aspect-square overflow-hidden rounded-2xl bg-navy/5 shadow-sm"
          >
            <img src={photoUrl(ph)} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-navy/0 transition-colors group-hover:bg-navy/20" />
          </button>
        ))}
      </div>
    </div>
  );
}

function Lightbox({ photos, index, onClose, onNav }) {
  const handleKey = useCallback((e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowRight") onNav(1);
    if (e.key === "ArrowLeft") onNav(-1);
  }, [onClose, onNav]);

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", handleKey); document.body.style.overflow = ""; };
  }, [handleKey]);

  return (
    <motion.div
      data-testid="lightbox"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/95 backdrop-blur-sm"
      onClick={onClose}
    >
      <button data-testid="lightbox-close" onClick={onClose} className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><X size={22} /></button>
      <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); onNav(-1); }} className="absolute left-3 sm:left-6 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronLeft size={26} /></button>
      <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); onNav(1); }} className="absolute right-3 sm:right-6 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronRight size={26} /></button>
      <motion.img
        key={photos[index]}
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
        src={photoUrl(photos[index])} alt="" onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
      />
      <span className="absolute bottom-6 rounded-full bg-white/10 px-3 py-1 text-sm text-white">{index + 1} / {photos.length}</span>
    </motion.div>
  );
}

export default function RealizationDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lb, setLb] = useState({ open: false, photos: [], index: 0 });

  useEffect(() => {
    setP(null); setNotFound(false);
    fetchRealization(id).then(setP).catch(() => setNotFound(true));
  }, [id]);

  const allPhotos = p ? [...(p.photos_before || []), ...(p.photos_during || []), ...(p.photos_after || [])] : [];
  const openLightbox = (ph) => setLb({ open: true, photos: allPhotos, index: Math.max(0, allPhotos.indexOf(ph)) });
  const navLightbox = (d) => setLb((s) => ({ ...s, index: (s.index + d + s.photos.length) % s.photos.length }));

  const share = () => {
    const url = window.location.href;
    const text = `Découvrez ce projet de TIRERA GROUP : ${p.title}`;
    if (navigator.share) {
      navigator.share({ title: p.title, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, "_blank");
    }
  };
  const copyLink = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Lien copié"); };

  if (notFound) {
    return (
      <main className="grid min-h-[70vh] place-items-center bg-white px-6 pt-24 text-center">
        <div>
          <Building2 className="mx-auto mb-4 text-navy/20" size={48} />
          <p className="text-navy/60">Cette réalisation est introuvable ou n'est plus publiée.</p>
          <Link to="/realisations" className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white"><ArrowLeft size={16} /> Toutes nos réalisations</Link>
        </div>
      </main>
    );
  }

  if (!p) return <main className="grid min-h-[70vh] place-items-center bg-white pt-24"><Loader2 className="animate-spin text-navy/40" /></main>;

  const cover = p.cover || allPhotos[0];

  return (
    <main data-testid="realization-detail" className="bg-white">
      <title>{`${p.title} | Réalisations TIRERA GROUP`}</title>
      <meta name="description" content={(p.description || `${categoryLabel(p.category)} — ${p.location || "Mali"}`).slice(0, 160)} />
      <meta property="og:title" content={`${p.title} | TIRERA GROUP`} />
      <meta property="og:description" content={(p.description || categoryLabel(p.category)).slice(0, 160)} />
      <meta property="og:type" content="article" />
      {cover && <meta property="og:image" content={photoUrl(cover)} />}

      {/* Hero cover */}
      <section className="relative h-[52vh] min-h-[380px] w-full overflow-hidden bg-navy">
        {cover && <img src={photoUrl(cover)} alt={p.title} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/20" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-10 sm:px-8">
          <Link to="/realisations" data-testid="back-to-realisations" className="mb-auto mt-28 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20">
            <ArrowLeft size={16} /> Nos réalisations
          </Link>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-gold px-3 py-1 text-xs font-bold text-navy">{categoryLabel(p.category)}</span>
              <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur">{p.status || "Terminé"}</span>
            </div>
            <h1 className="max-w-3xl font-display text-3xl font-bold leading-tight text-white sm:text-5xl">{p.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-white/80">
              {p.location && <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {p.location}</span>}
              {p.year && <span className="inline-flex items-center gap-1.5"><Calendar size={15} /> {p.year}</span>}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:grid lg:grid-cols-[1fr_320px] lg:gap-12">
        {/* Contenu */}
        <div>
          {p.description && (
            <div className="prose max-w-none">
              <h2 className="font-display text-2xl font-bold text-navy">À propos du projet</h2>
              <p className="mt-3 whitespace-pre-wrap text-navy/70 leading-relaxed">{p.description}</p>
            </div>
          )}

          {p.services?.length > 0 && (
            <div className="mt-8">
              <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-navy"><Hammer size={18} className="text-gold-700" /> Services réalisés</h3>
              <div className="flex flex-wrap gap-2">
                {p.services.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-navy/10 bg-navy/[0.03] px-3.5 py-1.5 text-sm font-medium text-navy/80">
                    <CheckCircle2 size={14} className="text-emerald-500" /> {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          <GallerySection title="Avant" photos={p.photos_before} onOpen={openLightbox} />
          <GallerySection title="Pendant" photos={p.photos_during} onOpen={openLightbox} />
          <GallerySection title="Après" photos={p.photos_after} onOpen={openLightbox} />
        </div>

        {/* Sidebar infos */}
        <aside className="mt-10 lg:mt-0">
          <div className="sticky top-24 rounded-3xl border border-navy/8 bg-white p-6 shadow-[0_10px_40px_-20px_rgba(11,31,58,0.35)]">
            <h3 className="font-display text-lg font-semibold text-navy">Fiche du projet</h3>
            <div className="mt-5 space-y-4">
              <InfoItem icon={Building2} label="Catégorie" value={categoryLabel(p.category)} />
              <InfoItem icon={MapPin} label="Lieu" value={p.location} />
              <InfoItem icon={Calendar} label="Année" value={p.year} />
              <InfoItem icon={CheckCircle2} label="Statut" value={p.status} />
              <InfoItem icon={Ruler} label="Surface / étages" value={p.size} />
              <InfoItem icon={Clock} label="Durée des travaux" value={p.duration} />
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={share} data-testid="share-realization" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-600"><Share2 size={15} /> Partager</button>
              <button onClick={copyLink} className="rounded-full border border-navy/15 px-4 py-2.5 text-sm font-medium text-navy hover:bg-navy/5">Copier le lien</button>
            </div>
            <a
              href={`https://wa.me/${BUSINESS_WHATSAPP}?text=${encodeURIComponent(`Bonjour TIRERA GROUP, je suis intéressé par un projet similaire à « ${p.title} ».`)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="quote-from-realization"
              className="mt-3 flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-3 text-sm font-bold text-navy hover:bg-gold-400"
            >
              Demander un projet similaire
            </a>
          </div>
        </aside>
      </div>

      <AnimatePresence>
        {lb.open && <Lightbox photos={lb.photos} index={lb.index} onClose={() => setLb((s) => ({ ...s, open: false }))} onNav={navLightbox} />}
      </AnimatePresence>
    </main>
  );
}
